import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
    importProvidersFrom, LOCALE_ID,
} from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { MatTabsModule } from '@angular/material/tabs';
import { provideKeycloakAndInterceptor } from "./core/auth/keycloak.config";
import { includeBearerTokenInterceptor } from "keycloak-angular";
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { environment } from "../environments/environment";
import { getAnalytics, provideAnalytics } from "@angular/fire/analytics";
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { AppNativeDateAdapter, patchNativeDateAdapterParse } from './core/date/app-native-date.adapter';
import { installGlobalDateInputMask } from './core/date/date-input-mask.initializer';

registerLocaleData(localeFr);

export const appConfig: ApplicationConfig = {
    providers: [
        provideKeycloakAndInterceptor(),
        provideHttpClient(withInterceptors([includeBearerTokenInterceptor])),
        importProvidersFrom(MatTabsModule),
        provideAnimations(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        {
            provide:
                LOCALE_ID, useValue: 'fr-FR'
        },
        provideNativeDateAdapter(),
        {

            provide: MAT_DATE_LOCALE,
            useValue: 'fr-FR',
        },
        {
            provide: DateAdapter,
            useClass: AppNativeDateAdapter,
        },
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);
            return firstValueFrom(translocoService.load(defaultLang));
        }),
        provideAppInitializer(() => {
            const doc = inject(DOCUMENT);
            installGlobalDateInputMask(doc);
        }),
        provideAppInitializer(() => {
            patchNativeDateAdapterParse();
        }),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAnalytics(() => getAnalytics()),
        provideMessaging(() => getMessaging()),
    ],
};
